import { EXTERNAL_REPO_SAFE_DEFAULTS } from "./externalRepoIntegrationTypes.js";
import { listSevenAppCapabilityManifests, } from "./sevenAppCapabilityManifest.js";
const BASE_APPROVAL_STEPS = [
    "owner_approval",
    "explicit_repo_review",
    "adapter_review",
    "tests_passing",
    "secrets_configured",
    "staging_environment_confirmed",
    "dry_run_validation",
    "rollback_plan_accepted",
];
const LEGACY_EXTERNAL_REPO_METADATA = {
    smart_print: {
        id: "external_repo_01_smart_print",
        expectedRepoName: "smart-print",
        kind: "business_app",
        riskLevel: "high",
        secretRefs: ["SMART_PRINT_API_KEY_REF", "SMART_PRINT_WEBHOOK_SECRET_REF"],
    },
    smart_salary: {
        id: "external_repo_02_smart_salary",
        expectedRepoName: "smart-salary-app",
        kind: "business_app",
        riskLevel: "high",
        secretRefs: [
            "SMART_SALARY_SUPABASE_URL_REF",
            "SMART_SALARY_SUPABASE_ANON_KEY_REF",
        ],
        additionalApprovalSteps: [
            "payroll_safety_review",
            "pii_safety_review",
            "tenant_safety_review",
            "rls_safety_review",
        ],
    },
    drishti_kundli: {
        id: "external_repo_03_drishti_kundli",
        expectedRepoName: "drishti-kundali-matching",
        kind: "business_app",
        riskLevel: "high",
        secretRefs: ["DRISHTI_KUNDLI_API_KEY_REF"],
    },
    anshika_api: {
        id: "external_repo_04_anshika_api",
        expectedRepoName: "anshika-saas-api-webhook",
        kind: "api_service",
        riskLevel: "critical",
        secretRefs: [
            "ANSHIKA_API_KEY_REF",
            "ANSHIKA_WEBHOOK_SECRET_REF",
            "ANSHIKA_SUPABASE_URL_REF",
            "ANSHIKA_SUPABASE_ANON_KEY_REF",
        ],
        additionalApprovalSteps: [
            "webhook_safety_review",
            "whatsapp_safety_review",
            "payment_safety_review",
            "secret_rotation_confirmed",
        ],
    },
    smart_ordering_core: {
        id: "external_repo_05_smart_ordering_core",
        expectedRepoName: "smart-ordering-core",
        kind: "business_app",
        riskLevel: "critical",
        secretRefs: [
            "SMART_ORDERING_AUTH_SECRET_REF",
            "SMART_ORDERING_DATABASE_URL_REF",
            "SMART_ORDERING_WEBHOOK_SECRET_REF",
            "SMART_ORDERING_RAZORPAY_KEY_REF",
            "SMART_ORDERING_SMART_PRINT_API_KEY_REF",
        ],
    },
    anoopam_press_website: {
        id: "external_repo_06_anoopam_press_website",
        expectedRepoName: "anoopam-press-website",
        kind: "business_app",
        riskLevel: "critical",
        secretRefs: [
            "ANOOPAM_SUPABASE_URL_REF",
            "ANOOPAM_SUPABASE_ANON_KEY_REF",
            "ANOOPAM_SUPABASE_SERVICE_ROLE_KEY_REF",
            "ANOOPAM_RAZORPAY_KEY_REF",
            "ANOOPAM_RAZORPAY_SECRET_REF",
            "ANOOPAM_SHIPROCKET_EMAIL_REF",
            "ANOOPAM_SHIPROCKET_PASSWORD_REF",
        ],
        additionalApprovalSteps: [
            "supabase_safety_review",
            "auth_safety_review",
            "customer_pii_safety_review",
            "order_safety_review",
            "design_file_safety_review",
            "payment_safety_review",
            "shipping_safety_review",
            "admin_permission_safety_review",
            "ai_provider_safety_review",
            "voice_provider_safety_review",
            "webhook_safety_review",
            "migration_safety_review",
            "storage_safety_review",
            "public_contact_data_safety_review",
            "production_readiness_review",
        ],
    },
    anshika_website_maker: {
        id: "external_repo_07_anshika_website_maker",
        expectedRepoName: "anshika-digital-website-maker",
        kind: "business_app",
        riskLevel: "critical",
        secretRefs: [
            "ANSHIKA_WEBSITE_MAKER_SUPABASE_URL_REF",
            "ANSHIKA_WEBSITE_MAKER_SUPABASE_ANON_KEY_REF",
            "ANSHIKA_WEBSITE_MAKER_DEPLOY_TOKEN_REF",
        ],
        additionalApprovalSteps: [
            "publish_safety_review",
            "domain_dns_safety_review",
            "supabase_safety_review",
            "customer_pii_safety_review",
        ],
    },
};
export const LEGACY_EXTERNAL_REPO_ID_BY_APP_ID = Object.freeze(Object.fromEntries(Object.entries(LEGACY_EXTERNAL_REPO_METADATA).map(([appId, metadata]) => [
    appId,
    metadata.id,
])));
function toCompatibilityProjection(capability) {
    const metadata = LEGACY_EXTERNAL_REPO_METADATA[capability.appId];
    return {
        id: metadata.id,
        displayName: capability.displayName,
        description: `Compatibility projection for ${capability.appId}; canonical capability authority is ${capability.canonicalAdapter}.`,
        expectedRepoName: metadata.expectedRepoName,
        kind: metadata.kind,
        riskLevel: metadata.riskLevel,
        status: "adapter_registered",
        adapterMode: "dry_run_adapter",
        capabilities: ["preview_only"],
        requiredApprovalSteps: [
            ...BASE_APPROVAL_STEPS,
            ...(metadata.additionalApprovalSteps ?? []),
        ],
        secretRefs: [...metadata.secretRefs],
        rollbackSupported: true,
        disableSwitchSupported: true,
        ...EXTERNAL_REPO_SAFE_DEFAULTS,
    };
}
export const EXTERNAL_REPO_INTEGRATION_REGISTRY = listSevenAppCapabilityManifests().map(toCompatibilityProjection);
export function listExternalRepoIntegrations() {
    return EXTERNAL_REPO_INTEGRATION_REGISTRY;
}
export function getExternalRepoIntegrationById(id) {
    return EXTERNAL_REPO_INTEGRATION_REGISTRY.find((entry) => entry.id === id);
}
export function getExternalRepoIntegrationsByStatus(status) {
    return EXTERNAL_REPO_INTEGRATION_REGISTRY.filter((entry) => entry.status === status);
}
export function getExternalRepoIntegrationPreview(id) {
    const entry = getExternalRepoIntegrationById(id);
    if (!entry)
        return { error: "not_found", id };
    return {
        id: entry.id,
        displayName: entry.displayName,
        status: entry.status,
        adapterMode: entry.adapterMode,
        dryRunOnly: entry.dryRunOnly,
        previewOnly: entry.previewOnly,
        realIntegrationEnabled: entry.realIntegrationEnabled,
        realExecutionPerformed: entry.realExecutionPerformed,
        riskLevel: entry.riskLevel,
        capabilities: entry.capabilities,
    };
}
export function validateExternalRepoIntegrationRegistry(entries = EXTERNAL_REPO_INTEGRATION_REGISTRY) {
    const errors = [];
    const ids = new Set();
    if (entries.length !== 7) {
        errors.push(`Registry must have exactly 7 entries, got ${entries.length}`);
    }
    for (const entry of entries) {
        if (ids.has(entry.id))
            errors.push(`Duplicate id: ${entry.id}`);
        ids.add(entry.id);
        if (!entry.dryRunOnly)
            errors.push(`${entry.id}: dryRunOnly must be true`);
        if (!entry.previewOnly)
            errors.push(`${entry.id}: previewOnly must be true`);
        if (!entry.requiresOwnerApproval) {
            errors.push(`${entry.id}: requiresOwnerApproval must be true`);
        }
        if (!entry.requiresExplicitRepoReview) {
            errors.push(`${entry.id}: requiresExplicitRepoReview must be true`);
        }
        if (!entry.requiresSecretsConfigured) {
            errors.push(`${entry.id}: requiresSecretsConfigured must be true`);
        }
        if (!entry.rollbackSupported) {
            errors.push(`${entry.id}: rollbackSupported must be true`);
        }
        if (!entry.disableSwitchSupported) {
            errors.push(`${entry.id}: disableSwitchSupported must be true`);
        }
        if (entry.realIntegrationEnabled) {
            errors.push(`${entry.id}: realIntegrationEnabled must be false`);
        }
        if (entry.realExecutionSupported) {
            errors.push(`${entry.id}: realExecutionSupported must be false`);
        }
        if (entry.realExecutionPerformed) {
            errors.push(`${entry.id}: realExecutionPerformed must be false`);
        }
        if (entry.networkAllowed) {
            errors.push(`${entry.id}: networkAllowed must be false`);
        }
        if (entry.fileMutationAllowed) {
            errors.push(`${entry.id}: fileMutationAllowed must be false`);
        }
        if (entry.commandExecutionAllowed) {
            errors.push(`${entry.id}: commandExecutionAllowed must be false`);
        }
        if (entry.browserActionAllowed) {
            errors.push(`${entry.id}: browserActionAllowed must be false`);
        }
        if (entry.desktopActionAllowed) {
            errors.push(`${entry.id}: desktopActionAllowed must be false`);
        }
        if (entry.productionDbAllowed) {
            errors.push(`${entry.id}: productionDbAllowed must be false`);
        }
        if (entry.ragVectorMutationAllowed) {
            errors.push(`${entry.id}: ragVectorMutationAllowed must be false`);
        }
        if (entry.adapterMode === "live") {
            errors.push(`${entry.id}: adapterMode must not be "live"`);
        }
        if (entry.status === "connected") {
            errors.push(`${entry.id}: status must not be "connected"`);
        }
        for (const ref of entry.secretRefs) {
            if (/^(sk-[a-zA-Z0-9-]{10,}|ghp_[a-zA-Z0-9_]{10,}|github_pat_[a-zA-Z0-9_]{10,}|xox[baprs]-[a-zA-Z0-9-]{10,})/.test(ref)) {
                errors.push(`${entry.id}: secretRef "${ref}" looks like a real secret`);
            }
        }
    }
    return { valid: errors.length === 0, errors };
}
export function assertNoLiveExternalRepoIntegrations(entries = EXTERNAL_REPO_INTEGRATION_REGISTRY) {
    const liveEntries = entries.filter((entry) => entry.realIntegrationEnabled ||
        entry.realExecutionSupported ||
        entry.realExecutionPerformed ||
        entry.networkAllowed ||
        entry.fileMutationAllowed ||
        entry.commandExecutionAllowed ||
        entry.browserActionAllowed ||
        entry.desktopActionAllowed ||
        entry.productionDbAllowed ||
        entry.ragVectorMutationAllowed ||
        entry.adapterMode === "live" ||
        entry.status === "connected");
    if (liveEntries.length > 0) {
        throw new Error(`assertNoLiveExternalRepoIntegrations: live/unsafe entries found: ${liveEntries.map((entry) => entry.id).join(", ")}`);
    }
}
//# sourceMappingURL=externalRepoIntegrationRegistry.js.map